/**
 * TanStack Query hooks for the 4 P0 screens.
 *
 * One hook per resource so screens stay declarative. Mutations are deliberately
 * not auto-retried (mutations.retry=0 in main.tsx) — signatures and writes
 * must remain user-deliberate per UX spec §4.1.
 *
 * Owner: Claude 3 (Frontend & UX).
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from './client';

// ---- Types (mirror mock-api seed; replace with generated OpenAPI types later) ----

export interface CurrentUser {
  id: string;
  full_name: string;
  initials: string;
  role: string;
  department: string;
  tenant: string;
  /** All Keycloak realm roles for this user. Used by RBAC gates. */
  roles?: string[];
}

export interface DocumentSummary {
  id: string;
  doc_number: string;
  doc_type_code: string;
  title: string;
  status: string;
  last_saved: string;
  /** Owning department — drives auto reviewer-chain routing (DMS-11). */
  department_id?: string | null;
}

export interface ActiveWorkflow {
  id: string;
  doc_number: string;
  stage: string;
  sla: string;
  sla_state: 'ok' | 'warn' | 'breach';
}

export interface MyTask {
  id: string;
  label: string;
  urgent: boolean;
  due: string;
}

export interface TrainingStats {
  completion_pct: number;
  overdue_count: number;
}

export interface WorkflowDetail {
  workflow_id: string;
  version_id: string;
  doc_number: string;
  title: string;
  version: string;
  previous_version: string;
  my_role: string;
  change_rationale: string;
  ai_redline: {
    enabled: boolean;
    summary: string;
    confidence: number;
    flagged_injection: boolean;
  };
  paragraphs: { id: string; text: string; state: 'unchanged' | 'modified' | 'added' | 'removed' }[];
  /** Recorded reviewer decision (null until a decision is made). */
  decision?: {
    decision: 'APPROVED' | 'REJECTED' | 'CHANGES_REQUESTED';
    status: string;
    decided_at: string;
    decided_by: string;
    reason: string | null;
  } | null;
}

export type WorkflowDecisionKind = 'APPROVED' | 'REJECTED' | 'CHANGES_REQUESTED';
export interface WorkflowDecisionResult {
  workflow_id: string;
  decision: WorkflowDecisionKind;
  status: string;
  decided_at: string;
  decided_by: string;
  reason: string | null;
}

export function useWorkflowDecision(workflowId: string) {
  const qc = useQueryClient();
  return useMutation<WorkflowDecisionResult, Error, { decision: WorkflowDecisionKind; comment?: string; reauthPassword?: string }>({
    mutationFn: ({ decision, comment, reauthPassword }) =>
      apiFetch<WorkflowDecisionResult>(`/api/v1/workflows/${workflowId}/decision`, {
        method: 'POST',
        // ESG-02 — reauth_password is the Part-11 sign-time identity proof,
        // verified server-side against Keycloak; never stored.
        body: { decision, comment, reauth_password: reauthPassword },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (data) => {
      // Update the cached detail immediately (keyed per workflow) so the screen
      // reflects the decision without a flicker — and never leaks across ids.
      qc.setQueryData<WorkflowDetail>(['workflows', workflowId], (old) =>
        old ? { ...old, decision: { decision: data.decision, status: data.status, decided_at: data.decided_at, decided_by: data.decided_by, reason: data.reason } } : old);
    },
  });
}

/** DMS-22: withdraw an in-flight workflow with a justification (audit trail). */
export function useWorkflowWithdraw(workflowId: string) {
  const qc = useQueryClient();
  return useMutation<{ workflow_id: string; state: string; reason: string }, Error, { reason: string }>({
    mutationFn: ({ reason }) =>
      apiFetch(`/api/v1/workflows/${workflowId}/withdraw`, {
        method: 'POST',
        body: { reason },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflows', workflowId] });
    },
  });
}

/**
 * QuizQuestion — TRN-002 invariant: the wire DTO MUST NOT include
 * `correct_option_id`. Grading is server-side via POST
 * /api/v1/document-versions/{id}/quiz/answers. If a future change adds
 * the field back here, the 04-r-and-u-reader.spec.ts assertion will fail.
 */
export interface QuizQuestion {
  id: string;
  prompt: string;
  options: { id: string; text: string }[];
  // correct_option_id intentionally absent — TRN-002 invariant.
}

export interface ReadPayload {
  id: string;
  doc_number: string;
  title: string;
  version: string;
  effective_date: string;
  is_critical_sop: boolean;
  body_paragraphs: string[];
  quiz: QuizQuestion[];
}

export interface AuditEvent {
  audit_id: string;
  event_at_utc: string;
  entity_type: string;
  entity_id: string;
  event_code: string;
  actor_name: string;
  ip_address: string;
}

// ---- Queries ----

export function useCurrentUser(): UseQueryResult<CurrentUser> {
  return useQuery({
    queryKey: ['users', 'me'],
    queryFn: ({ signal }) => apiFetch<CurrentUser>('/api/v1/users/me', { signal }),
    staleTime: 5 * 60_000,
  });
}

export function useDocuments(): UseQueryResult<{ items: DocumentSummary[]; next_cursor: string | null }> {
  return useQuery({
    queryKey: ['documents'],
    queryFn: ({ signal }) =>
      apiFetch<{ items: DocumentSummary[]; next_cursor: string | null }>('/api/v1/documents', { signal }),
  });
}

export function useActiveWorkflows() {
  return useQuery({
    queryKey: ['workflows', 'active'],
    queryFn: ({ signal }) => apiFetch<{ items: ActiveWorkflow[] }>('/api/v1/workflows/active', { signal }),
  });
}

export function useMyTasks() {
  return useQuery({
    queryKey: ['tasks', 'me'],
    queryFn: ({ signal }) => apiFetch<{ items: MyTask[] }>('/api/v1/tasks/me', { signal }),
  });
}

export function useTrainingStats() {
  return useQuery({
    queryKey: ['training-tasks', 'me', 'stats'],
    queryFn: ({ signal }) => apiFetch<TrainingStats>('/api/v1/training-tasks/me/stats', { signal }),
  });
}

export function useWorkflowDetail(workflowId: string) {
  return useQuery({
    queryKey: ['workflows', workflowId],
    queryFn: ({ signal }) => apiFetch<WorkflowDetail>(`/api/v1/workflows/${workflowId}`, { signal }),
    enabled: !!workflowId,
  });
}

export function useReadPayload(versionId: string) {
  return useQuery({
    queryKey: ['document-versions', versionId, 'read-payload'],
    queryFn: ({ signal }) => apiFetch<ReadPayload>(`/api/v1/document-versions/${versionId}/read-payload`, { signal }),
    enabled: !!versionId,
  });
}

export interface AuditEventFilters {
  from?: string;
  to?: string;
  event_code?: string;
  actor?: string;
}

export function useAuditEvents(filters: AuditEventFilters) {
  return useQuery({
    queryKey: ['audit', 'events', filters],
    queryFn: ({ signal }) =>
      apiFetch<{ items: AuditEvent[]; total: number }>('/api/v1/audit/events', {
        query: filters as Record<string, string | number | undefined>,
        signal,
      }),
  });
}

// ---- Mutations ----

export interface CreateDocumentInput {
  doc_type_code: string;
  title: string;
}

export interface CreateDocumentResponse {
  id: string;
  document_id: string;
  doc_number: string;
  major_version: number;
  minor_version: number;
  status: string;
}

export function useCreateDocument() {
  return useMutation({
    mutationFn: (input: CreateDocumentInput) =>
      apiFetch<CreateDocumentResponse>('/api/v1/documents', {
        method: 'POST',
        body: input,
        idempotencyKey: newIdempotencyKey(),
      }),
  });
}

// ============================================================================
// useStartWorkflow — Phase 1 closure (T-P1-W3-06).
// POST /api/v1/workflows with Idempotency-Key. 24h replay window per WF-008.
// ============================================================================

export interface StartWorkflowInput {
  document_version_id: string;
  template_code: string;
  reviewer_user_ids: string[];
  rationale: string;
}

export interface StartWorkflowResponse {
  workflow_id: string;
  document_version_id: string;
  state: string;
  version: number;
  initiator_user_id: string;
  started_at_utc: string;
}

export function useStartWorkflow() {
  return useMutation({
    mutationFn: (input: StartWorkflowInput) =>
      apiFetch<StartWorkflowResponse>('/api/v1/workflows', {
        method: 'POST',
        body: input,
        idempotencyKey: newIdempotencyKey(),
      }),
  });
}

export interface SignatureChallengeInput {
  target_type: 'workflow_step' | 'training_task' | 'periodic_review' | 'withdrawal' | 'tenant_config';
  target_id: string;
  signature_meaning: string;
}

export interface SignatureChallengeResponse {
  challenge_token: string;
  challenge_expires_at: string;
  required_factors: string[];
  attestation_text: string;
  attestation_version: number;
  target_type: string;
  target_id: string;
  signature_meaning: string;
}

export function useRequestSignatureChallenge() {
  return useMutation({
    mutationFn: (input: SignatureChallengeInput) =>
      apiFetch<SignatureChallengeResponse>('/api/v1/signatures/challenge', {
        method: 'POST',
        body: input,
        idempotencyKey: newIdempotencyKey(),
      }),
  });
}

// ============================================================================
// QA Workbench hooks (Wave 2 / T-P1-W3-08)
// ============================================================================

export type QAPendingWorkflow = {
  workflowId: string;
  documentNumber: string;
  title: string;
  authorName: string;
  initiatorName: string;
  timeInState: string;
  estimatedSignaturesRequired: number;
  hasAiSuggestion: boolean;
};

export type QARejectedWorkflow = {
  workflowId: string;
  documentNumber: string;
  rejectedAt: string;
};

export type TrainingImpactWarning = {
  workflowId: string;
  documentNumber: string;
  estimatedSignaturesRequired: number;
};

export type AiSuggestion = {
  workflowId: string;
  summary: string;
  concerns: string[];
};

export function useQAPendingApprovals() {
  return useQuery<QAPendingWorkflow[]>({
    queryKey: ['qa', 'pending-approvals'],
    queryFn: () => apiFetch<QAPendingWorkflow[]>('/api/v1/qa/pending-approvals'),
  });
}

export function useQARejectedRecent() {
  return useQuery<QARejectedWorkflow[]>({
    queryKey: ['qa', 'rejected-recent'],
    queryFn: () => apiFetch<QARejectedWorkflow[]>('/api/v1/qa/recently-rejected'),
  });
}

export function useTrainingImpactWarnings() {
  return useQuery<TrainingImpactWarning[]>({
    queryKey: ['qa', 'training-impact'],
    queryFn: () => apiFetch<TrainingImpactWarning[]>('/api/v1/qa/training-impact-warnings'),
  });
}

export function useAiSuggestion(workflowId: string, enabled: boolean) {
  return useQuery<AiSuggestion>({
    queryKey: ['ai-suggestion', workflowId],
    queryFn: () => apiFetch<AiSuggestion>(`/api/v1/ai/suggestions/${workflowId}`),
    enabled,
  });
}

export function useViewAiSuggestion() {
  return useMutation<{ ok: true }, Error, { workflowId: string }>({
    mutationFn: ({ workflowId }) =>
      apiFetch<{ ok: true }>(`/api/v1/ai/suggestions/${workflowId}/view`, {
        method: 'POST',
        idempotencyKey: newIdempotencyKey(),
      }),
  });
}

export function useRequestConference() {
  return useMutation<
    { conferenceId: string },
    Error,
    { workflowId: string; scheduledFor: string; note: string }
  >({
    mutationFn: ({ workflowId, scheduledFor, note }) =>
      apiFetch<{ conferenceId: string }>(`/api/v1/workflows/${workflowId}/conference-request`, {
        method: 'POST',
        body: { scheduledFor, note },
        idempotencyKey: newIdempotencyKey(),
      }),
  });
}

// ============================================================================
// Phase 3 hooks — notification + webhook + tenant-config
// Owner: Claude 3 (Frontend & UX).
// ============================================================================

// ----- Notifications -----

export interface NotificationItem {
  id: string;
  tenant_id: string;
  recipient_user_id: string;
  channel: 'EMAIL' | 'SMS' | 'IN_APP';
  template_key: string;
  body_text: string | null;
  state: 'QUEUED' | 'SENT' | 'FAILED' | 'READ' | 'SUPPRESSED' | 'DEFERRED';
  created_at_utc: string;
  sent_at_utc: string | null;
  read_at_utc: string | null;
}

export function useNotificationInbox() {
  return useQuery<{ items: NotificationItem[] }>({
    queryKey: ['notifications', 'inbox'],
    queryFn: () => apiFetch<{ items: NotificationItem[] }>('/api/v1/notifications/inbox'),
  });
}

export interface NotificationPreferencesPayload {
  EMAIL?: { enabled: boolean };
  IN_APP?: { enabled: boolean };
  SMS?: { enabled: boolean };
  quiet_hours?: string | null;
}

export function useNotificationPreferences() {
  return useQuery<NotificationPreferencesPayload>({
    queryKey: ['notifications', 'preferences'],
    queryFn: () => apiFetch<NotificationPreferencesPayload>('/api/v1/notifications/preferences'),
  });
}

export function useMarkNotificationRead() {
  return useMutation<{ ok: true }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiFetch<{ ok: true }>(`/api/v1/notifications/${id}/read`, {
        method: 'POST',
        idempotencyKey: newIdempotencyKey(),
      }),
  });
}

export function useUpdateNotificationPreferences() {
  return useMutation<{ ok: true }, Error, { channel: 'EMAIL' | 'SMS' | 'IN_APP'; enabled: boolean }>({
    mutationFn: (input) =>
      apiFetch<{ ok: true }>('/api/v1/notifications/preferences', {
        method: 'POST',
        body: input,
      }),
  });
}

// ----- Webhooks -----

export interface WebhookSubscriptionView {
  webhook_id: string;
  tenant_id: string;
  target_url: string;
  event_types: string[];
  secret_version: number;
  created_at_utc: string;
}

export function useWebhooks() {
  return useQuery<{ items: WebhookSubscriptionView[] }>({
    queryKey: ['webhooks'],
    queryFn: () => apiFetch<{ items: WebhookSubscriptionView[] }>('/api/v1/webhooks'),
  });
}

export function useCreateWebhook() {
  return useMutation<WebhookSubscriptionView, Error, { target_url: string; event_types: string[] }>({
    mutationFn: (input) =>
      apiFetch<WebhookSubscriptionView>('/api/v1/webhooks', {
        method: 'POST',
        body: input,
        idempotencyKey: newIdempotencyKey(),
      }),
  });
}

export function useDeleteWebhook() {
  return useMutation<{ ok: true }, Error, { webhookId: string }>({
    mutationFn: ({ webhookId }) =>
      apiFetch<{ ok: true }>(`/api/v1/webhooks/${webhookId}`, {
        method: 'DELETE',
      }),
  });
}

export function useRotateWebhookSecret() {
  return useMutation<{ webhook_id: string; secret_version: number }, Error, { webhookId: string }>({
    mutationFn: ({ webhookId }) =>
      apiFetch<{ webhook_id: string; secret_version: number }>(
        `/api/v1/webhooks/${webhookId}/rotate-secret`,
        { method: 'POST', idempotencyKey: newIdempotencyKey() },
      ),
  });
}

// ----- Tenant config -----

export interface TenantConfigEntry {
  key: string;
  value: string;
  sensitivity: 'PUBLIC' | 'PRIVATE' | 'GMP_CRITICAL';
  version: number;
  signature_bundle_ids?: string[];
}

export function useTenantConfigSnapshot() {
  return useQuery<Record<string, TenantConfigEntry>>({
    queryKey: ['tenant-config', 'snapshot'],
    queryFn: () => apiFetch<Record<string, TenantConfigEntry>>('/api/v1/tenant-config'),
  });
}

// ============================================================================
// Phase 4 hooks — training tasks
// ============================================================================

export interface TrainingTaskItem {
  task_id: string;
  user_id: string;
  document_version_id: string;
  doc_number: string;
  title: string;
  state: 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE' | 'SUPERSEDED';
  assigned_at_utc: string;
  due_date: string;
  completed_at_utc: string | null;
  signature_id: string | null;
}

export function useMyTrainingTasks() {
  return useQuery<{ items: TrainingTaskItem[] }>({
    queryKey: ['training-tasks', 'me'],
    queryFn: () => apiFetch<{ items: TrainingTaskItem[] }>('/api/v1/training-tasks/me'),
  });
}

// ============================================================================
// Phase 4.5 hooks — OCR + full-text search
// ============================================================================

export interface OcrSearchHit {
  document_version_id: string;
  doc_number: string;
  doc_title: string;
  page_number: number;
  snippet: string;
  confidence_score: number;
  language_detected: 'en' | 'ar' | 'ar+en';
  is_ocr: true;                 // always true on this endpoint
  flagged_for_review: boolean;
  document_state: string;
}

export interface OcrReviewItem {
  page_id: string;
  document_id: string;
  document_version_id: string;
  doc_number: string;
  doc_title?: string;
  page_number: number;
  page_count?: number;
  document_state?: string;
  text_content: string;
  /** Low-confidence substrings to highlight in the extracted text. */
  flagged_terms?: string[];
  confidence_score: number;
  language_detected: 'en' | 'ar' | 'ar+en';
}

export function useOcrSearch(query: string, includeWithdrawn = false) {
  return useQuery<{ items: OcrSearchHit[]; query: string; total: number }>({
    queryKey: ['ocr', 'search', query, includeWithdrawn],
    queryFn: () =>
      apiFetch<{ items: OcrSearchHit[]; query: string; total: number }>(
        '/api/v1/ocr/search',
        { query: { q: query, include_withdrawn: includeWithdrawn ? 'true' : 'false' } },
      ),
    enabled: query.trim().length >= 2,
  });
}

export function useOcrReviewQueue() {
  return useQuery<{ items: OcrReviewItem[] }>({
    queryKey: ['ocr', 'review-queue'],
    queryFn: () => apiFetch<{ items: OcrReviewItem[] }>('/api/v1/ocr/review-queue'),
  });
}

export function useApplyOcrCorrection() {
  return useMutation<{ ok: true }, Error, { pageId: string; correctedText: string }>({
    mutationFn: ({ pageId, correctedText }) =>
      apiFetch<{ ok: true }>(`/api/v1/ocr/pages/${pageId}/correction`, {
        method: 'POST',
        body: { corrected_text: correctedText },
        idempotencyKey: newIdempotencyKey(),
      }),
  });
}

/** Lightweight document list for pickers (OCR run trigger, etc.). */
export interface DocumentLite {
  document_id: string;
  doc_number: string;
  title: string;
}
export function useDocumentsLite() {
  return useQuery<{ items: DocumentLite[] }>({
    queryKey: ['documents', 'lite'],
    queryFn: () => apiFetch<{ items: DocumentLite[] }>('/api/v1/documents'),
    staleTime: 60_000,
  });
}

/** Queue a (simulated) OCR run for a scanned page — persists a real
 *  ocr.runs + ocr.pages row that flows into the review queue. (OCR-FN.) */
export function useQueueOcrRun() {
  return useMutation<
    { run_id: string; page_id: string; flagged_for_review: boolean; confidence_score: number },
    Error,
    { documentId: string; pageText: string; language?: string }
  >({
    mutationFn: ({ documentId, pageText, language }) =>
      apiFetch('/api/v1/ocr/runs', {
        method: 'POST',
        body: { documentId, pageText, language: language ?? 'en' },
        idempotencyKey: newIdempotencyKey(),
      }),
  });
}
